<template>
  <div class="partner-tab-population-filter">
    <PopulationFilterOperatorRow
      label="Visibility"
      :operator="selection.operator"
      @select="updateSelection({ operator: $event })"
    />
    <div class="partner-tab-population-filter__levels">
      <div class="font-bold text-neutral-text-strong">Sharing level</div>
      <div class="text-sm text-neutral-text-weak">
        Select sharing levels and population types.
      </div>
      <div
        class="flex flex-wrap gap-4 mt-8"
        role="group"
        aria-label="Sharing level"
      >
        <button
          v-for="level in SHARING_LEVELS_BY_SIDE[side]"
          :key="level.value"
          type="button"
          class="partner-tab-population-filter__level"
          :class="
            isSelected(level.value)
              ? SHARING_ACTIVE_CLASSES[level.value]
              : INACTIVE_CLASSES
          "
          :aria-pressed="isSelected(level.value)"
          @click="toggleLevel(level.value)"
        >
          {{ level.label }}
        </button>
      </div>
    </div>
    <PopulationFilterTree
      v-if="side === 'mine'"
      :selected-filters="segmentFilters"
      :is-saved-filter="false"
      @update="updateSelection({ items: $event.items })"
    />
    <PartnerPopulationFilterTree
      v-else
      :selected-filters="segmentFilters"
      :partner-org-ids="partnerOrgIds"
      :is-saved-filter="false"
      @update="updateSelection({ items: $event.items })"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import PartnerPopulationFilterTree from '@/components/reports/PartnerPopulationFilterTree.vue';
import PopulationFilterOperatorRow from '@/components/reports/PopulationFilterOperatorRow.vue';
import PopulationFilterTree from '@/components/reports/PopulationFilterTree.vue';

import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  SHARING_ACTIVE_CLASSES,
  type SharingType,
} from '@/constants/data_shares';
import type { ReportPartnerSegmentFilters } from '@/types/reports';

import type { PopulationFilterSelectionType } from '../types';

type PopulationSideType = 'partner' | 'mine';
type SharingLevelType = { label: string; value: SharingType };

const { side, partnerOrgIds } = defineProps<{
  side: PopulationSideType;
  partnerOrgIds?: number[];
}>();

/**
 * OLAPS_ONLY holds aggregates_only, which is the Counts level, not Overlaps.
 * A partner's hidden populations never reach the client, so offering Hidden
 * on their side would match nothing.
 */
const SHARED_LEVELS: SharingLevelType[] = [
  { label: 'All', value: GREENFIELD_SHARING },
  { label: 'Overlaps', value: SHARING },
  { label: 'Counts', value: OLAPS_ONLY },
];

const SHARING_LEVELS_BY_SIDE: Record<PopulationSideType, SharingLevelType[]> = {
  partner: SHARED_LEVELS,
  mine: [...SHARED_LEVELS, { label: 'Hidden', value: HIDDEN }],
};

const INACTIVE_CLASSES =
  'border-transparent bg-neutral-background text-neutral-text hover:border-neutral-border';

const selection = defineModel<PopulationFilterSelectionType>('selection', {
  required: true,
});

const operator = computed(() => selection.value.operator);
const items = computed(() => selection.value.items);
const segmentFilters = computed<ReportPartnerSegmentFilters>(() => ({
  operator: operator.value,
  items: items.value,
}));

function updateSelection(changes: Partial<PopulationFilterSelectionType>) {
  selection.value = { ...selection.value, ...changes };
}

function isSelected(level: SharingType) {
  return selection.value.levels.includes(level);
}

function toggleLevel(level: SharingType) {
  const { levels } = selection.value;
  updateSelection({
    levels: isSelected(level)
      ? levels.filter((selected) => selected !== level)
      : [...levels, level],
  });
}
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-population-filter {
  @apply w-[400px];
}

.partner-tab-population-filter__levels {
  @apply flex flex-col p-8 border-b border-neutral-border;
}

.partner-tab-population-filter__level {
  @apply px-8 py-2 rounded-full border text-sm transition-colors;

  &:focus-visible {
    @apply outline-hidden outline-2 outline-neutral-border-focus;
  }
}
</style>
